import _ from 'lodash';

export const DEFAULT_NETMASK_OPTIONS = [
    { value: 30, text: '/30 (255.255.255.252)' },
    { value: 29, text: '/29 (255.255.255.248)' },
    { value: 28, text: '/28 (255.255.255.240)' },
    { value: 27, text: '/27 (255.255.255.224)' },
    { value: 26, text: '/26 (255.255.255.192)' },
    { value: 25, text: '/25 (255.255.255.128)' },
    { value: 24, text: '/24 (255.255.255.0)' },
    { value: 23, text: '/23 (255.255.254.0)' },
];

export const DEFAULT_FINDER_VALUES = {
    startIp: '10.250.0.0',
    endIp: '10.250.0.255',
    netmask: 24,
    serverUrl: '',
    totalHostCount: '256',
};

export function normalizeServerUrl(rawValue) {
    const trimmedValue = typeof rawValue === 'string' ? rawValue.trim() : '';
    if (!trimmedValue) {
        return '';
    }

    const valueWithProtocol = /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;

    try {
        const parsedUrl = new URL(valueWithProtocol);
        if (!parsedUrl.pathname || parsedUrl.pathname === '/') {
            parsedUrl.pathname = '/npoint';
        }

        return parsedUrl.toString().replace(/\/$/, '');
    } catch (error) {
        return valueWithProtocol.replace(/\/$/, '');
    }
}

export function checkIp(ipAddress) {
    return typeof ipAddress === 'string' && /\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ipAddress);
}

export function ip2long(ipAddress) {
    if (!checkIp(ipAddress)) {
        return false;
    }

    const parts = ipAddress.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
    if (!parts) {
        return false;
    }

    return Number(parts[1]) * 16777216 + Number(parts[2]) * 65536 + Number(parts[3]) * 256 + Number(parts[4]);
}

export function long2ip(longIp) {
    const firstOctet = Math.floor(longIp / 16777216) % 256;
    const secondOctet = Math.floor(longIp / 65536) % 256;
    const thirdOctet = Math.floor(longIp / 256) % 256;
    const fourthOctet = longIp % 256;

    return [firstOctet, secondOctet, thirdOctet, fourthOctet].join('.');
}

export function cidrToRange(cidr) {
    const parts = cidr.split('/');
    if (parts.length === 1 || !checkIp(parts[0])) {
        return false;
    }

    const netmask = Number(parts[1]);
    if (Number.isNaN(netmask) || netmask < 0 || netmask > 32) {
        return false;
    }

    if (netmask === 32) {
        return [parts[0], parts[0]];
    }

    const longStart = ip2long(parts[0]);
    const blockSize = 2 ** (32 - netmask);
    const network = Math.floor(longStart / blockSize) * blockSize;
    const broadcast = network + blockSize - 1;

    return [long2ip(network), long2ip(broadcast)];
}

export function countIps(startIp, endIp) {
    const start = ip2long(startIp);
    const end = ip2long(endIp);

    if (start === false || end === false) {
        return 0;
    }

    return end - start + 1;
}

export function listIps(startIp, endIp) {
    const ipList = [];
    if (!checkIp(startIp) || !checkIp(endIp)) {
        return ipList;
    }

    const start = ip2long(startIp);
    const end = ip2long(endIp);
    for (let ipNumber = start; ipNumber <= end; ipNumber += 1) {
        ipList.push(long2ip(ipNumber));
    }

    return ipList;
}

export function calculateEndIp(startIp, netmask) {
    if (!startIp || netmask === undefined || netmask === null) {
        return '';
    }

    const range = cidrToRange(`${startIp}/${netmask}`);
    return range?.[1] || '';
}

function splitSemver(version) {
    const pieces = typeof version === 'string' ? version.split('.') : [];

    return {
        major: parseInt(pieces[0], 10) || 0,
        minor: parseInt(pieces[1], 10) || 0,
        patch: parseInt(pieces[2], 10) || 0,
    };
}

export function isSemanticVersion(version) {
    if (!version) {
        return false;
    }

    return version.split('.').length > 3;
}

export function getProtocol(device) {
    const version = splitSemver(device?.version);
    return version.major === 3 ? 'https://' : 'http://';
}

export function canAutoconfig(version) {
    if (isSemanticVersion(version)) {
        return true;
    }

    const parsedVersion = splitSemver(version);
    const minimumVersion = splitSemver('2.3.1119');

    if (parsedVersion.major === '----------') {
        return false;
    }
    if (parsedVersion.major < minimumVersion.major) {
        return false;
    }
    if (parsedVersion.major === minimumVersion.major && parsedVersion.minor < minimumVersion.minor) {
        return false;
    }
    if (
        parsedVersion.major === minimumVersion.major &&
        parsedVersion.minor === minimumVersion.minor &&
        parsedVersion.patch < minimumVersion.patch
    ) {
        return false;
    }

    return true;
}

export function macExists(devices, newDevice) {
    if (!_.isArray(devices) || devices.length === 0 || !newDevice?.mac) {
        return false;
    }

    return !_.isEmpty(_.find(devices, (existingDevice) => existingDevice.mac === newDevice.mac));
}

export function sha1Hex(value) {
    /* eslint-disable no-bitwise */
    function rotateLeft(bits, shift) {
        return (bits << shift) | (bits >>> (32 - shift));
    }

    function toHexString(valueToEncode) {
        let output = '';

        for (let index = 7; index >= 0; index -= 1) {
            output += ((valueToEncode >>> (index * 4)) & 0x0f).toString(16);
        }

        return output;
    }

    const utf8Value = unescape(encodeURIComponent(value));
    const wordArray = [];

    for (let index = 0; index < utf8Value.length - 3; index += 4) {
        const word =
            (utf8Value.charCodeAt(index) << 24) |
            (utf8Value.charCodeAt(index + 1) << 16) |
            (utf8Value.charCodeAt(index + 2) << 8) |
            utf8Value.charCodeAt(index + 3);
        wordArray.push(word);
    }

    let tailWord = 0;
    switch (utf8Value.length % 4) {
        case 0:
            tailWord = 0x080000000;
            break;
        case 1:
            tailWord = (utf8Value.charCodeAt(utf8Value.length - 1) << 24) | 0x0800000;
            break;
        case 2:
            tailWord =
                (utf8Value.charCodeAt(utf8Value.length - 2) << 24) |
                (utf8Value.charCodeAt(utf8Value.length - 1) << 16) |
                0x08000;
            break;
        case 3:
            tailWord =
                (utf8Value.charCodeAt(utf8Value.length - 3) << 24) |
                (utf8Value.charCodeAt(utf8Value.length - 2) << 16) |
                (utf8Value.charCodeAt(utf8Value.length - 1) << 8) |
                0x80;
            break;
        default:
            break;
    }

    wordArray.push(tailWord);

    while (wordArray.length % 16 !== 14) {
        wordArray.push(0);
    }

    wordArray.push(utf8Value.length >>> 29);
    wordArray.push((utf8Value.length << 3) & 0x0ffffffff);

    const schedule = new Array(80);
    let hashA = 0x67452301;
    let hashB = 0xefcdab89;
    let hashC = 0x98badcfe;
    let hashD = 0x10325476;
    let hashE = 0xc3d2e1f0;

    for (let blockStart = 0; blockStart < wordArray.length; blockStart += 16) {
        for (let scheduleIndex = 0; scheduleIndex < 16; scheduleIndex += 1) {
            schedule[scheduleIndex] = wordArray[blockStart + scheduleIndex];
        }

        for (let scheduleIndex = 16; scheduleIndex < 80; scheduleIndex += 1) {
            schedule[scheduleIndex] = rotateLeft(
                schedule[scheduleIndex - 3] ^
                    schedule[scheduleIndex - 8] ^
                    schedule[scheduleIndex - 14] ^
                    schedule[scheduleIndex - 16],
                1
            );
        }

        let currentA = hashA;
        let currentB = hashB;
        let currentC = hashC;
        let currentD = hashD;
        let currentE = hashE;

        for (let round = 0; round < 80; round += 1) {
            let tempValue;

            if (round < 20) {
                tempValue =
                    (rotateLeft(currentA, 5) +
                        ((currentB & currentC) | (~currentB & currentD)) +
                        currentE +
                        schedule[round] +
                        0x5a827999) &
                    0x0ffffffff;
            } else if (round < 40) {
                tempValue =
                    (rotateLeft(currentA, 5) +
                        (currentB ^ currentC ^ currentD) +
                        currentE +
                        schedule[round] +
                        0x6ed9eba1) &
                    0x0ffffffff;
            } else if (round < 60) {
                tempValue =
                    (rotateLeft(currentA, 5) +
                        ((currentB & currentC) | (currentB & currentD) | (currentC & currentD)) +
                        currentE +
                        schedule[round] +
                        0x8f1bbcdc) &
                    0x0ffffffff;
            } else {
                tempValue =
                    (rotateLeft(currentA, 5) +
                        (currentB ^ currentC ^ currentD) +
                        currentE +
                        schedule[round] +
                        0xca62c1d6) &
                    0x0ffffffff;
            }

            currentE = currentD;
            currentD = currentC;
            currentC = rotateLeft(currentB, 30);
            currentB = currentA;
            currentA = tempValue;
        }

        hashA = (hashA + currentA) & 0x0ffffffff;
        hashB = (hashB + currentB) & 0x0ffffffff;
        hashC = (hashC + currentC) & 0x0ffffffff;
        hashD = (hashD + currentD) & 0x0ffffffff;
        hashE = (hashE + currentE) & 0x0ffffffff;
    }
    /* eslint-enable no-bitwise */

    return `${toHexString(hashA)}${toHexString(hashB)}${toHexString(hashC)}${toHexString(hashD)}${toHexString(hashE)}`;
}
